import React, { forwardRef } from "react";
import { cn, useInput } from "@heroui/react";
import { useFormStatus } from "react-dom";

const styles = {
  label: "text-black/50 dark:text-white/90",
  input: [
    "bg-transparent",
    "text-black/90 dark:text-white/90",
    "placeholder:text-default-700/50 dark:placeholder:text-white/60",
  ],
  innerWrapper: "bg-transparent",
  inputWrapper: [
    "shadow-xl",
    "bg-default-200/50",
    "dark:bg-default/60",
    "backdrop-blur-xl",
    "backdrop-saturate-200",
    "hover:bg-default-200/70",
    "focus-within:!bg-default-200/50",
    "dark:hover:bg-default/70",
    "dark:focus-within:!bg-default/60",
    "!cursor-text",
  ],
};

const FieldsetInput = forwardRef<HTMLInputElement | HTMLTextAreaElement>((props, ref) =>
{
  const {
    Component,
    label,
    domRef,
    description,
    isClearable,
    startContent,
    endContent,
    shouldLabelBeOutside,
    shouldLabelBeInside,
    errorMessage,
    getBaseProps,
    getLabelProps,
    getInputProps,
    getInnerWrapperProps,
    getInputWrapperProps,
    getDescriptionProps,
    getErrorMessageProps,
    getClearButtonProps,
  } = useInput({
    ...props,
    ref,
  });

  const labelContent = (
    <label
      {...getLabelProps()}
      className={
        "pointer-events-none absolute z-10 block max-w-full origin-top-left cursor-text overflow-hidden text-ellipsis px-[0.55rem] text-small text-foreground-500 subpixel-antialiased transition-[transform,color,left,opacity] !duration-200 !ease-out will-change-auto group-data-[filled-within=true]:pointer-events-auto group-data-[filled-within=true]:-translate-y-[calc(100%+4px)] group-data-[filled-within=true]:text-default-600 group-data-[invalid=true]:text-danger group-data-[focus-within=true]:text-primary motion-reduce:transition-none rtl:origin-top-right"
      }
    >
      {label}
    </label>
  );

  const innerWrapper = React.useMemo(() => {
    if (startContent || endContent) {
      return (
        <div className="flex h-full w-full items-center justify-between">
          {startContent}
          <input {...getInputProps()} />
          {endContent}
        </div>
      );
    }

    return <input {...getInputProps()} />;
  }, [startContent, getInputProps, getInnerWrapperProps]);

  return (
    <Component {...getBaseProps()}>
      {shouldLabelBeOutside ? labelContent : null}
      <div
        // {...getInputWrapperProps()}
        className="relative flex h-14 flex-col items-start justify-center px-4 !outline-none"
        role="button"
        onClick={() => {
          domRef.current?.focus();
        }}
      >
        {shouldLabelBeInside ? labelContent : null}
        {innerWrapper}
        <fieldset
          aria-hidden="true"
          className="pointer-events-none absolute inset-[-4px_0px_0px] size-full rounded-medium px-4 border-1 group-data-[focus-within=true]:border-medium group-data-[focus-within=true]:border-primary group-data-[invalid=true]:border-danger"
        >
          <legend className="invisible max-w-[0.01px] text-nowrap text-small group-data-[filled-within=true]:max-w-full group-data-[filled-within=true]:px-2">
            {label}
          </legend>
        </fieldset>
      </div>
      {description && <div {...getDescriptionProps()}>{description}</div>}
      {errorMessage && (
        <div {...getErrorMessageProps()}>
          <span className="pl-1">{errorMessage}</span>{" "}
        </div>
      )}
    </Component>
  );
});

FieldsetInput.displayName = "FieldsetInput";

export default FieldsetInput;
